import { Thread } from '@langchain/langgraph-sdk';
import {
  ChevronDownIcon,
  InfoCircleIcon,
  XIcon,
} from '@langchain/untitled-ui-icons';

import { LucideSparkles } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';
import useSWRMutation from 'swr/mutation';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import LoadingSlimIcon from '@/Pages/Graph/icons/LoadingSlimIcon.svg?react';
import { useCopy } from '@/components/CopyButton/useCopy';
import { DeleteConfirmationButton } from '@/components/Delete';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { SkeletonRows } from '@/components/Skeleton';
import useToast from '@/components/Toast/useToast';
import { Button, IconButton, Input, Text, Tooltip } from '@/design-system';
import CheckIcon from '@/icons/CheckIcon.svg?react';
import CopyIcon from '@/icons/CopyIcon.svg?react';
import EditIcon from '@/icons/EditIcon.svg?react';
import MessageChatIcon from '@/icons/MessageChatIcon.svg?react';
import ProgressStarIcon from '@/icons/ProgressStarIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { useLangGraphClient } from '../../api';
import {
  useGraphCancelAllPendingRuns,
  useGraphThread,
  useGraphThreads,
} from '../../api/useGraphSwr';
import { StateContext } from '../../control/state';
import { useActiveThreadId } from '../../hooks/useActiveThreadId';
import { RtfAuto } from '../debug/common/relative-time';

export function GraphThreadEdit(props: {
  id: string;
  defaultValue: string | undefined;
  placeholder: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  'use no memo';
  const [langgraph, apiUrl] = useLangGraphClient();
  const form = useForm<{ name: string }>({
    defaultValues: { name: props.defaultValue },
  });

  const mutate = useSWRMutation(
    [apiUrl, 'threads', props.id],
    async ([, , id], { arg }: { arg: string }) => {
      await langgraph.threads.update(id, {
        metadata: { __name__: arg },
      });
      await props.onSuccess();
    }
  );

  const submit = form.handleSubmit((data) => mutate.trigger(data.name));

  return (
    <span
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="mb-1 grid grid-cols-[1fr,auto] gap-1"
    >
      <input
        type="text"
        placeholder={props.placeholder}
        autoFocus
        className="rounded-md border border-secondary bg-transparent p-0 px-1.5 py-0.5 focus:border-brand"
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            submit(e);
          }
        }}
        {...form.register('name')}
      />
      <div className="flex flex-row items-center gap-1">
        <IconButton
          label="Save"
          onClick={submit}
          disabled={form.formState.isSubmitting}
          icon={form.formState.isSubmitting ? LoadingSlimIcon : CheckIcon}
          color="secondary"
          variant="outlined"
        />
        <IconButton
          onClick={props.onCancel}
          label="Cancel"
          icon={XIcon}
          color="secondary"
          variant="outlined"
        />
      </div>
    </span>
  );
}

function GraphThreadItem(props: {
  thread: Thread;
  onSuccess?: () => void;
  setIsListOpen: (isListOpen: boolean) => void;
}) {
  const state = useContext(StateContext);
  const [editName, setEditName] = useState(false);

  const metadata = z
    .object({ graph_id: z.string() })
    .safeParse(props.thread.metadata);
  const userName =
    typeof props.thread.metadata?.['__name__'] === 'string'
      ? props.thread.metadata?.['__name__'] || undefined
      : undefined;

  const name = userName || props.thread.thread_id;
  const { copied, onCopy } = useCopy({ copy: props.thread.thread_id });

  const { setThreadId } = useActiveThreadId();

  return (
    <div className="group grid grid-cols-[1fr,auto] py-0">
      <button
        type="button"
        onClick={() => {
          setThreadId(props.thread.thread_id);
          state.setRunState({
            type: props.thread.status === 'busy' ? 'running' : 'completed',
          });
          props.setIsListOpen(false);
        }}
        className="grid grid-cols-[auto,1fr] items-center gap-3 pb-3 pt-2 text-left"
      >
        <span
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-tertiary',
            state.threadId === props.thread.thread_id &&
              'bg-brand-tertiary text-brand-green-400',
            props.thread.status === 'error' && 'bg-error-primary'
          )}
        >
          {props.thread.status === 'error' ? (
            <InfoCircleIcon className="h-5 w-5 text-error" />
          ) : (
            <MessageChatIcon className="h-5 w-5" />
          )}
        </span>

        <span className="flex flex-col text-left">
          <span>
            {editName ? (
              <GraphThreadEdit
                id={props.thread.thread_id}
                defaultValue={userName}
                placeholder={props.thread.thread_id}
                onSuccess={() => {
                  setEditName(false);
                  return props.onSuccess?.();
                }}
                onCancel={() => setEditName(false)}
              />
            ) : (
              <span className="grid grid-cols-[1fr,auto,auto] items-center gap-1">
                <span className="overflow-hidden truncate">{name}</span>
                <IconButton
                  label="Copy ID"
                  tabIndex={-1}
                  className="opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCopy();
                  }}
                  icon={copied ? CheckIcon : CopyIcon}
                  color="secondary"
                  variant="outlined"
                />
                <IconButton
                  label="Edit name"
                  tabIndex={-1}
                  className="opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditName(true);
                  }}
                  icon={EditIcon}
                  color="secondary"
                  variant="outlined"
                />
              </span>
            )}
          </span>

          <span className="flex items-center gap-1">
            <span>
              {metadata.success && (
                <span className="rounded-md border border-secondary px-1 py-0.5 text-sm text-tertiary">
                  {metadata.data.graph_id}
                </span>
              )}
            </span>
            <span className="text-sm text-tertiary">
              <RtfAuto time={new Date(props.thread.updated_at)} />
            </span>
            {props.thread.status === 'busy' && (
              <Tooltip title="Thread is busy">
                <span>
                  <ProgressStarIcon className="h-4 w-4 animate-spin text-brand-green-400" />
                </span>
              </Tooltip>
            )}
          </span>
        </span>
      </button>
    </div>
  );
}

function GraphPendingRunsCancelAll() {
  const { createToast } = useToast();
  const cancel = useGraphCancelAllPendingRuns({
    onSuccess: (cancelled) => {
      createToast({
        type: 'success',
        title: 'All pending runs cancelled',
        description: `${cancelled} run${cancelled === 1 ? '' : 's'} cancelled`,
      });
    },
  });
  return (
    <DeleteConfirmationButton
      title="Cancel all pending runs"
      description="Are you sure you want to cancel all pending runs?"
      deleteButtonText="Cancel all pending runs"
      isDeleting={cancel.isMutating}
      onDelete={() => cancel.trigger()}
      trigger={
        <Button variant="normal" color="secondary">
          Cancel all pending runs
        </Button>
      }
    />
  );
}

function GraphThreadList(props: {
  scrollParentRef: HTMLDivElement | null;
  setIsListOpen: (isListOpen: boolean) => void;
}) {
  const {
    threads,
    hasMore,
    isLoading: isThreadsLoading,
    isValidating: isThreadsValidating,
    error: threadsError,
    mutate: mutateThreads,
    size,
    setSize,
  } = useGraphThreads({});

  const state = useContext(StateContext);

  const { threadId: activeThreadId } = useActiveThreadId();
  const selectedThreadIndex = useMemo(() => {
    return threads?.findIndex((i) => i.thread_id === activeThreadId) ?? -1;
  }, [threads, activeThreadId]);

  const [searchThreadId, setSearchThreadId] = useState<string | undefined>();
  const [debouncedSearchThreadId] = useDebounce(searchThreadId, 500);

  const { data: searchedThread, error: searchedThreadError } = useGraphThread(
    debouncedSearchThreadId
  );

  const { setThreadId } = useActiveThreadId();

  if (isThreadsLoading) {
    return (
      <div className="p-4">
        <SkeletonRows
          rows={10}
          skeletonClassName="h-[70px]"
          className="gap-0"
        />
      </div>
    );
  }

  if (threadsError) {
    return (
      <div className="p-4">
        <ExpandableErrorAlert error={threadsError} />
      </div>
    );
  }

  if (!threads?.length) {
    return (
      <div className="my-8 flex flex-col items-center justify-center">
        <div className="border-1 mb-4 rounded-2xl border border-secondary p-5">
          <LucideSparkles className="text-gray-400" />
        </div>
        <span className="mt-2 text-sm text-gray-400">
          No threads created yet
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="mx-4 my-2 flex flex-col">
        <Virtuoso
          customScrollParent={props.scrollParentRef ?? undefined}
          data={threads}
          initialTopMostItemIndex={Math.max(selectedThreadIndex, 0)}
          itemContent={(_index, thread) => (
            <GraphThreadItem
              key={thread.thread_id}
              thread={thread}
              onSuccess={() => mutateThreads()}
              setIsListOpen={props.setIsListOpen}
            />
          )}
        />
        {hasMore && (
          <LoadMoreButton
            isInitialLoad
            isLoading={isThreadsValidating}
            onClick={() => setSize(size + 1)}
            isVirtual
          />
        )}
      </div>
      <div className="sticky bottom-0 flex items-center gap-2 border-t border-secondary bg-popover p-4">
        <Popover>
          <PopoverTrigger>
            <Button variant="normal" color="secondary">
              Open thread via ID
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex w-[400px] flex-col gap-1.5"
          >
            <form
              className="flex items-start gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (searchedThread) {
                  setThreadId(searchedThread.thread_id);
                  state.setRunState({
                    type: 'completed',
                  });
                  props.setIsListOpen(false);
                }
              }}
            >
              <Input
                type="text"
                name="threadId"
                placeholder="Thread ID"
                value={searchThreadId}
                onChange={(value) => setSearchThreadId(value)}
                size="sm"
                hintText={
                  searchedThreadError
                    ? 'Thread not found. Please enter a valid UUID.'
                    : undefined
                }
                isError={!!searchedThreadError}
              />
              <Button type="submit" disabled={!searchedThread}>
                Open
              </Button>
            </form>
          </PopoverContent>
        </Popover>

        <GraphPendingRunsCancelAll />
      </div>
    </>
  );
}

export function GraphThreadSelect({
  compactHeader,
}: {
  compactHeader: boolean;
}) {
  const { threadId } = useActiveThreadId();

  const { data: thread } = useGraphThread(threadId);

  const [isListOpen, setIsListOpen] = useState(false);

  const threadName =
    (thread?.metadata?.['__name__'] as string | undefined) ?? threadId;

  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  return (
    <Popover open={isListOpen} onOpenChange={setIsListOpen}>
      <PopoverTrigger asChild data-testid="graph-thread-select-trigger">
        <Button
          variant="plain"
          color="secondary"
          rightDecorator={ChevronDownIcon}
          className="min-w-0 flex-auto"
        >
          {threadId ? (
            <>
              <Tooltip
                title={compactHeader ? `Thread: ${threadId}` : undefined}
              >
                <Text variant="sm" weight="medium" className="flex-shrink-0">
                  Thread
                </Text>
              </Tooltip>
              {!compactHeader && (
                <Tooltip title={`Thread: ${threadId}`}>
                  <Text variant="sm" className="min-w-0 truncate">
                    {threadName}
                  </Text>
                </Tooltip>
              )}
            </>
          ) : (
            <Text variant="sm">New Thread</Text>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        ref={setRef}
        align="start"
        className={cn(
          'max-h-[70vh] min-w-[500px] overflow-y-auto px-0 py-0',
          !ref && 'opacity-0'
        )}
      >
        <GraphThreadList scrollParentRef={ref} setIsListOpen={setIsListOpen} />
      </PopoverContent>
    </Popover>
  );
}
